import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Search, Filter, Star, MapPin, DollarSign, 
  Music, Camera, Mic2, Megaphone, Smile, ChefHat, 
  Cake, Palette, Sparkles, Shield, BadgeCheck
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { API } from "../App";

const categoryIcons = {
  dj: Music,
  musician: Mic2,
  singer: Mic2,
  mc: Megaphone,
  comedian: Smile,
  photographer: Camera,
  caterer: ChefHat,
  baker: Cake,
  decorator: Palette,
  balloon_artist: Sparkles,
  security: Shield,
};

const categoryImages = {
  dj: "https://images.unsplash.com/photo-1695277714272-9ec10733de30?w=400&h=300&fit=crop",
  musician: "https://images.unsplash.com/photo-1769755012867-920ce0d8f7f4?w=400&h=300&fit=crop",
  singer: "https://images.unsplash.com/photo-1695277579050-dd6754f41ea2?w=400&h=300&fit=crop",
  photographer: "https://images.unsplash.com/photo-1763848843590-acd070c8b9ae?w=400&h=300&fit=crop",
  caterer: "https://images.unsplash.com/photo-1764380746818-18c01e96df12?w=400&h=300&fit=crop",
  baker: "https://images.unsplash.com/photo-1729875749490-cb5984d780ec?w=400&h=300&fit=crop",
  decorator: "https://images.unsplash.com/photo-1540228294755-ba7db9385b06?w=400&h=300&fit=crop",
};

const VendorMarketplace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  const selectedCategory = searchParams.get("category") || "";

  useEffect(() => {
    fetchCategories();
    fetchVendors();
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/vendors/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const params = selectedCategory ? { category: selectedCategory } : {};
      const response = await axios.get(`${API}/vendors`, { params });
      setVendors(response.data);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (value) => {
    if (value === "all") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", value);
    }
    setSearchParams(searchParams);
  };

  const filteredVendors = vendors.filter(vendor => 
    vendor.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryIcon = (category) => {
    const IconComponent = categoryIcons[category] || Sparkles;
    return IconComponent;
  };

  const getVendorImage = (vendor) => {
    if (vendor.portfolio_images?.length > 0) {
      return vendor.portfolio_images[0];
    }
    return categoryImages[vendor.category] || "https://images.unsplash.com/photo-1710371281650-6164e4133ae7?w=400&h=300&fit=crop";
  };

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      {/* Header */}
      <section className="pt-28 pb-12 px-6">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
              Find Your <span className="text-gold">Perfect Vendor</span>
            </h1>
            <p className="text-white/60 max-w-xl mx-auto">
              Browse our curated selection of professional event vendors
            </p>
          </motion.div>
          
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4 max-w-3xl mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                data-testid="vendor-search-input"
                type="text"
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 bg-charcoal border-white/10 focus:border-gold text-white placeholder:text-white/40 rounded-full"
              />
            </div>
            <Select value={selectedCategory || "all"} onValueChange={handleCategoryChange}>
              <SelectTrigger 
                data-testid="category-filter"
                className="w-full md:w-48 h-12 bg-charcoal border-white/10 text-white rounded-full"
              >
                <Filter className="w-4 h-4 mr-2 text-gold" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-charcoal border-white/10">
                <SelectItem value="all" className="text-white hover:bg-white/5">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id} className="text-white hover:bg-white/5">
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>
      
      {/* Category Pills */}
      <section className="px-6 pb-8">
        <div className="container mx-auto">
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              data-testid="category-all"
              variant={!selectedCategory ? "default" : "outline"}
              onClick={() => handleCategoryChange("all")}
              className={`rounded-full ${!selectedCategory ? "bg-gold text-black" : "border-white/10 text-white/60 hover:border-gold hover:text-gold"}`}
            >
              All
            </Button>
            {categories.map((cat) => {
              const Icon = categoryIcons[cat.id] || Sparkles;
              return (
                <Button
                  key={cat.id}
                  data-testid={`category-pill-${cat.id}`}
                  variant={selectedCategory === cat.id ? "default" : "outline"}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`rounded-full ${selectedCategory === cat.id ? "bg-gold text-black" : "border-white/10 text-white/60 hover:border-gold hover:text-gold"}`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {cat.name}
                </Button>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Vendors Grid */}
      <section className="px-6 pb-24">
        <div className="container mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="text-center py-20">
              <Sparkles className="w-12 h-12 text-gold/30 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-white mb-2">No vendors found</h3>
              <p className="text-white/60">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVendors.map((vendor, index) => {
                const CategoryIcon = getCategoryIcon(vendor.category);
                return (
                  <motion.div
                    key={vendor.vendor_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link 
                      to={`/vendors/${vendor.vendor_id}`}
                      data-testid={`vendor-card-${vendor.vendor_id}`}
                      className="group relative overflow-hidden rounded-2xl bg-charcoal border border-white/5 hover:border-gold/50 transition-all duration-300 block"
                    >
                      {/* Image */}
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img 
                          src={getVendorImage(vendor)}
                          alt={vendor.business_name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                        
                        {/* Category badge */}
                        <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1">
                          <CategoryIcon className="w-4 h-4 text-gold" />
                          <span className="text-xs text-white capitalize">{vendor.category?.replace("_", " ")}</span>
                        </div>
                        
                        {/* Verified badge */}
                        {vendor.is_verified && (
                          <div className="absolute top-4 right-4 bg-gold rounded-full p-1">
                            <BadgeCheck className="w-4 h-4 text-black" />
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="p-5">
                        <h3 className="font-heading text-lg font-bold text-white mb-2 group-hover:text-gold transition-colors">
                          {vendor.business_name}
                        </h3>
                        
                        <p className="text-white/60 text-sm mb-4 line-clamp-2">
                          {vendor.description}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center text-white/60 text-sm">
                              <MapPin className="w-4 h-4 mr-1 text-gold/60" />
                              {vendor.location}
                            </div>
                          </div>
                          
                          <div className="flex items-center">
                            <Star className="w-4 h-4 text-gold fill-gold mr-1" />
                            <span className="text-white font-medium">{vendor.rating?.toFixed(1) || "New"}</span>
                            {vendor.review_count > 0 && (
                              <span className="text-white/40 text-sm ml-1">({vendor.review_count})</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                          <div className="flex items-center text-gold font-bold">
                            <DollarSign className="w-4 h-4" />
                            {vendor.hourly_rate}/hr
                          </div>
                          <span className="text-sm text-white/40 group-hover:text-gold transition-colors">
                            View Profile →
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default VendorMarketplace;
